/**
 * Fade-in helper
 * ---------------
 * Applies fade-in animations to elements with [data-fade].
 *
 * Behaviour:
 * - Adds .fade-in to <html> to enable CSS transitions.
 * - Adds .is-visible when element enters ~25% of the viewport.
 * - Skips animation if prefers-reduced-motion is set or if
 *      IntersectionObserver is not supported.
 * - Elements with [data-critical] are revealed immediately (no animation).
 * - Elements become visible on keyboard focus (focusin event).
 * - Optional per-element delay via [data-fade-delay="150"] (ms).
 * - By default animates once, override with [data-fade-once="false"].
 */


(() => {
    const root = document.documentElement;
    root.classList.add('fade-in');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fadeElements = Array.from(document.querySelectorAll('[data-fade]'));

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        fadeElements.forEach(el => el.classList.add('is-visible'));
        return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    fadeElements.forEach(element => {
        if (element.hasAttribute('data-critical')) {
            element.classList.add('is-visible');
            return;
        }
        const r = element.getBoundingClientRect();
        if (r.top < viewportHeight && r.bottom > 0) element.classList.add('is-visible');
    });

    document.addEventListener('focusin', (event) => {
        const element = event.target.closest('[data-fade]');
        if (element && !element.classList.contains('is-visible')) {
            element.classList.add('is-visible');
        }
    });

    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const element = entry.target;

            const delay = Number(element.getAttribute('data-fade-delay')) || 0;
            if (delay) element.style.transitionDelay = `${delay}ms`;

            element.classList.add('is-visible');

            const once = element.getAttribute('data-fade-once');
            if (once !== 'false') observer.unobserve(element);
        }
    }, {
        root: null,
        rootMargin: '-75% 0px -25% 0px',
        threshold: 0
    });

    fadeElements.forEach(element => {
        if (!element.classList.contains('is-visible') && !element.hasAttribute('data-critical')) {
            observer.observe(element);
        }
    });

    window.fadeObserve = function (elements) {
        (Array.isArray(elements) ? elements : [elements]).forEach(element => {
            if (!(element instanceof Element)) return;
            if (!element.matches('[data-fade]')) return;
            if (prefersReducedMotion) { element.classList.add('is-visible'); return; }
            element.classList.remove('is-visible');
            observer.observe(element);
        });
    };
})();
